import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Thermometer,
  Droplets,
  Sun,
  Utensils,
  HandMetal,
  HeartPulse,
  MapPin,
  Ruler,
  Clock,
  ShieldAlert,
  BookOpen,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";
import HeatGradientDiagram from "../components/HeatGradientDiagram";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatRow = ({ label, value, testid }) => (
  <div className="flex justify-between gap-3 py-2 border-b border-white/5 text-sm">
    <span className="text-[#F4F1DE]/50 shrink-0">{label}</span>
    <span className="text-[#F4F1DE] text-right" data-testid={testid}>{value}</span>
  </div>
);

const Section = ({ icon: Icon, title, children, accent = "text-[#E07A5F]", testid }) => (
  <div className="bg-[#121212] border border-white/10 p-6" data-testid={testid}>
    <div className="flex items-center gap-2 mb-4">
      <Icon className={`w-5 h-5 ${accent}`} />
      <h2 className="text-lg font-bold text-[#F4F1DE]">{title}</h2>
    </div>
    {children}
  </div>
);

const GuideDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const { data } = await axios.get(`${API}/guides/${slug}`);
        setGuide(data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] pt-24 flex justify-center"><div className="spinner" /></div>;
  }

  if (notFound || !guide) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] pt-24 pb-16 text-[#F4F1DE]" data-testid="guide-notfound">
        <div className="max-w-xl mx-auto px-4 text-center">
          <h1 className="text-2xl font-bold mb-3">Care guide not found</h1>
          <p className="text-[#F4F1DE]/60 mb-6">We haven't verified husbandry data for that species yet.</p>
          <Link to="/guides" className="text-[#E07A5F] hover:underline">← Back to all care guides</Link>
        </div>
      </div>
    );
  }

  // Rough guide on which ReptiCraft tier to recommend based on adult size
  const suggestedBuildPath = () => {
    // Parse width from "4 × 2 × 2 ft"
    const m = guide.enclosure_min_ft.match(/(\d+)\s*[×x]\s*(\d+)\s*[×x]\s*(\d+)/);
    if (!m) return "/configurator";
    const [w, h, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (w <= 6 && h <= 4 && d <= 2) return `/engineered?size=${w}x${h}x${d}`;
    return `/configurator?w=${w}&h=${h}&d=${d}`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F4F1DE] pt-24 pb-16" data-testid="guide-detail-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate("/guides")}
          className="flex items-center gap-1.5 text-xs text-[#F4F1DE]/60 hover:text-[#E07A5F] mb-6 uppercase tracking-widest"
          data-testid="back-to-guides"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All care guides
        </button>

        {/* Header */}
        <div className="mb-10 pb-8 border-b border-white/10 grid grid-cols-1 md:grid-cols-[1fr_260px] gap-6 items-start">
          <div>
            <div className="flex items-center gap-2 text-[#E07A5F] text-xs uppercase tracking-widest mb-3">
              <BookOpen className="w-4 h-4" />
              <span>Verified UK husbandry guide</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" data-testid="guide-common-name">
              {guide.common_name}
            </h1>
            <p className="text-lg italic text-[#F4F1DE]/50 mt-2" data-testid="guide-latin-name">{guide.latin_name}</p>

            <div className="mt-5 flex flex-wrap gap-2">
              <span className="text-xs px-2.5 py-1 border border-[#E07A5F]/40 text-[#E07A5F] uppercase tracking-wider">
                {guide.difficulty}
              </span>
              <span className="text-xs px-2.5 py-1 border border-white/15 text-[#F4F1DE]/70">
                <MapPin className="w-3 h-3 inline mr-1" />{guide.origin.split("—")[0].trim()}
              </span>
              <span className="text-xs px-2.5 py-1 border border-white/15 text-[#F4F1DE]/70">
                <Ruler className="w-3 h-3 inline mr-1" />{guide.adult_size_cm}
              </span>
              <span className="text-xs px-2.5 py-1 border border-white/15 text-[#F4F1DE]/70">
                <Clock className="w-3 h-3 inline mr-1" />{guide.lifespan_years}
              </span>
            </div>
          </div>

          {guide.image_url && (
            <figure className="relative aspect-[4/3] w-full overflow-hidden border border-white/10 bg-[#121212]" data-testid="guide-hero-image">
              <img
                src={guide.image_url}
                alt={`${guide.common_name} (${guide.latin_name})`}
                loading="lazy"
                onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                className="w-full h-full object-cover"
              />
              {guide.image_credit && (
                <figcaption className="absolute bottom-0 right-0 text-[9px] uppercase tracking-widest text-white/70 bg-black/50 px-1.5 py-0.5">
                  {guide.image_credit}
                </figcaption>
              )}
            </figure>
          )}
        </div>

        {/* CTA strip */}
        <div className="mb-10 p-5 bg-gradient-to-br from-[#E07A5F]/10 via-[#121212] to-[#121212] border border-[#E07A5F]/30 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#E07A5F]">Build your enclosure</p>
            <p className="text-base font-semibold text-[#F4F1DE]">
              Minimum {guide.enclosure_min_ft} — pre-configured for {guide.common_name}
            </p>
          </div>
          <Link
            to={suggestedBuildPath()}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#E07A5F] hover:bg-[#D06A4F] text-white font-bold text-sm transition-colors whitespace-nowrap"
            data-testid="guide-configure-cta"
          >
            Configure now <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Heat gradient visual diagram */}
        <div className="mb-5">
          <HeatGradientDiagram
            heatSource={guide.heat_source}
            basking={guide.temp_basking_surface_c}
            warmHide={guide.temp_warm_hide_c}
            ambient={guide.temp_ambient_cool_c}
            night={guide.temp_night_min_c}
            uvi={guide.uvi_target}
          />
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section icon={Thermometer} title="Temperature gradient" accent="text-amber-400" testid="guide-temp-section">
            <StatRow label="Basking surface" value={`${guide.temp_basking_surface_c} °C`} testid="guide-temp-basking" />
            <StatRow label="Warm hide" value={`${guide.temp_warm_hide_c} °C`} />
            <StatRow label="Ambient / cool side" value={`${guide.temp_ambient_cool_c} °C`} />
            <StatRow label="Night minimum" value={`${guide.temp_night_min_c} °C`} />
          </Section>

          <Section icon={Droplets} title="Humidity" accent="text-blue-400" testid="guide-humidity-section">
            <p className="text-sm text-[#F4F1DE]/80 leading-relaxed" data-testid="guide-humidity">
              {guide.humidity_percent}
            </p>
          </Section>

          <Section icon={Sun} title="UVB / lighting" accent="text-yellow-400" testid="guide-uvb-section">
            <StatRow label="Target UVI" value={guide.uvi_target} testid="guide-uvi" />
            <StatRow label="Recommended bulb" value={guide.uvb_bulb} />
            <StatRow label="Photoperiod (summer)" value={guide.photoperiod_summer} />
            <StatRow label="Photoperiod (winter)" value={guide.photoperiod_winter} />
          </Section>

          <Section icon={Utensils} title="Substrate & diet" accent="text-green-400" testid="guide-diet-section">
            <p className="text-xs uppercase tracking-widest text-[#F4F1DE]/50 mb-1">Substrate</p>
            <p className="text-sm text-[#F4F1DE]/80 mb-4">{guide.substrate}</p>
            <p className="text-xs uppercase tracking-widest text-[#F4F1DE]/50 mb-1">Diet</p>
            <p className="text-sm text-[#F4F1DE]/80">{guide.diet}</p>
          </Section>

          <Section icon={HandMetal} title="Handling & temperament" accent="text-purple-400">
            <p className="text-sm text-[#F4F1DE]/80 mb-2"><strong className="text-[#F4F1DE]">Temperament:</strong> {guide.temperament}</p>
            <p className="text-sm text-[#F4F1DE]/80">{guide.handling}</p>
          </Section>

          <Section icon={HeartPulse} title="Common health issues" accent="text-red-400">
            <p className="text-sm text-[#F4F1DE]/80">{guide.common_health_issues}</p>
          </Section>
        </div>

        {/* Sources / citations */}
        <div className="mt-10 p-5 bg-[#121212] border border-white/10" data-testid="guide-sources">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert className="w-4 h-4 text-[#F4F1DE]/60" />
            <p className="text-xs uppercase tracking-widest text-[#F4F1DE]/60 font-semibold">
              Sources this guide was verified against
            </p>
          </div>
          <ul className="space-y-1 text-sm text-[#F4F1DE]/70">
            {guide.sources.map((src) => (
              <li key={src} className="flex gap-2">
                <span className="text-[#E07A5F]">—</span>
                <span>{src}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-[#F4F1DE]/40 mt-4 leading-relaxed">
            This guide is informational and reflects modern UK husbandry consensus. It does not replace
            the advice of a qualified UK reptile vet. Always consult one if your animal is unwell.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GuideDetailPage;
